export function formatToCurrency(num: number) {
  if (num >= 10000000) {
    return `${(num / 10000000).toFixed(1)} Cr`;
  }
  if (num >= 100000) {
    return `${(num / 100000).toFixed(1)} L`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)} K`;
  }
  return num.toString();
}
/**
 * Minimal hierarchy shape shared by the two callers (ContractLinkedBoqItem and
 * EstimateItem). Kept generic so each caller gets its own item type back
 * instead of `any[]`.
 */
interface HierarchyNode {
  id?: string;
  parentsList?: Array<{ itemId?: string }>;
}

export const buildChildMap = <T extends HierarchyNode>(data: T[]): Record<string, T[]> => {
  const childMap: Record<string, T[]> = {};

  data.forEach((item) => {
    item.parentsList?.forEach((parent) => {
      const parentItemId = parent.itemId;
      if (parentItemId && parentItemId !== "0") {
        const siblings = childMap[parentItemId] ?? [];
        siblings.push(item);
        childMap[parentItemId] = siblings;
      }
    });
  });

  return childMap;
};

export const getAllChildrenOptimized = <T extends HierarchyNode>(
  id: string | undefined,
  childMap: Record<string, T[]>,
  visited: Set<string> = new Set(),
): T[] => {
  // Guard against cyclic parentsList data (a node reachable from itself), which
  // would otherwise recurse infinitely and freeze the tab. The shared `visited`
  // set also dedupes DAG re-visits, avoiding exponential blow-up on diamond graphs.
  if (!id || visited.has(id)) return [];
  visited.add(id);

  const children = childMap[id] ?? [];
  const result = [...children];

  for (const child of children) {
    result.push(...getAllChildrenOptimized(child.id, childMap, visited));
  }

  return result;
};
